import {
    Button,
    Grid,
    Input,
    Modal,
    useToastAlert,
} from '@design-systems/canvas-ui'
import Icon, { CommentIcon, ArrowUpIcon } from '@design-systems/react-icons'
import React, { useState } from 'react'
import { useCreateFeedback } from 'hooks/feedback/useCreateFeedback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import './feedback.scss'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useLocation } from 'react-router-dom'
import { useLogClick } from 'utils/analytics'

const FeedbackFooter: React.FC = () => {
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [feedbackState, setFeedbackState] = useState('')
    const { t, i18n } = useEssentials()
    const { feedback } = TRANSLATION_KEYS
    const makeToast = useToastAlert()
    const { logClick } = useLogClick('feedback')

    const sendFeedback = useCreateFeedback(
        () => {
            makeToast({
                variant: 'success',
                heading: t(feedback.thankYou),
                message: t(feedback.thankYouForSubmitting),
            })
        },
        () => {
            makeToast({
                variant: 'error',
                heading: t(feedback.error),
                message: t(feedback.errorSubmitting),
            })
        },
    )

    const openModal = () => {
        logClick('open feedback modal')
        setIsOpen(true)
    }

    const closeModal = () => {
        setIsOpen(false)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        logClick('submit feedback')
        sendFeedback(`${location.pathname}: ${feedbackState}`)
        setFeedbackState('')
        setIsOpen(false)
    }

    return (
        <>
            <button
                type="button"
                onClick={openModal}
                className="feedback_footer"
                aria-labelledby="feedback-button-label"
            >
                <span
                    id="feedback-button-label"
                    lang={i18n.language}
                    className="hc8-sr-only"
                    aria-hidden="true"
                >
                    {t(feedback.footerAriaLabel)}
                </span>
                <span className="feedback_footer_text">
                    {t(feedback.shareFeedback)}
                </span>
                <Icon svg={CommentIcon} size="sm" aria-hidden />
            </button>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                headingText={t(feedback.sendAMessage)}
                className="feedback_modal"
                scrollBehavior="contained"
                size="md"
            >
                <form onSubmit={handleSubmit}>
                    <div className="hc8-pa-3x">
                        <Grid.Container>
                            <Grid.Item size={{ xs: true }}>
                                <Input.Label>
                                    {t(feedback.sendAMessage)}
                                </Input.Label>
                                <Input.Textarea
                                    isRequired
                                    placeholder={t(feedback.message)}
                                    className="hc8-mt-2x"
                                    onChange={(
                                        e: React.ChangeEvent<HTMLTextAreaElement>,
                                    ) => setFeedbackState(e.target.value)}
                                    aria-label={t(feedback.sendAMessage)}
                                    value={feedbackState}
                                />
                                <Input.InlineMessage>
                                    {t(feedback.hereToHelp)}
                                </Input.InlineMessage>
                            </Grid.Item>
                        </Grid.Container>

                        <Grid.Container direction="row-reverse" spacing="dense">
                            <Grid.Item>
                                <Button
                                    type="submit"
                                    className="feedback_form_button"
                                    aria-label={t(feedback.submitFeedback)}
                                    isDisabled={!feedbackState.trim()}
                                    variant="primary"
                                >
                                    <Icon svg={ArrowUpIcon} aria-hidden />
                                </Button>
                            </Grid.Item>
                        </Grid.Container>
                    </div>
                </form>
            </Modal>
        </>
    )
}

export default FeedbackFooter
